// Analytics — distinct from Dashboard.
// Dashboard shows "now" snapshots. Analytics shows ratios, behaviour, and
// drill-down (heat-maps, asset journey, stale inventory, location flow).
//
// Layout (top → bottom):
//   1. Period picker
//   2. KPI tiles      — utilisation, accuracy, velocity, stale ratio, coverage
//   3. Activity heat-map (24 × 7 grid) + Lifecycle stacked bar
//   4. Stale assets table        + Top-moved assets table
//   5. Location coverage table   + User leaderboard
//   6. Location flow (Sankey-ish)+ Asset Journey (interactive picker)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { ChartTooltip, gridProps } from '../lib/chartTheme';
import {
  MdInsights, MdSpeed, MdLocationOn, MdSwapHoriz, MdSearch,
  MdHourglassEmpty, MdRefresh, MdTrendingUp, MdAssignment,
  MdEmojiEvents, MdRoute, MdInventory2, MdCompareArrows,
  MdEventNote, MdAccessTime, MdRouter, MdCalendarMonth,
} from 'react-icons/md';
import api from '../api/axios';
import ExportButtons from '../components/ExportButtons';
import useCountUp from '../lib/useCountUp';
import Skeleton from '../components/Skeleton';

const PERIODS = [
  { value: '24h',  label: '24 h' },
  { value: '7d',   label: '7 d'  },
  { value: '30d',  label: '30 d' },
  { value: '90d',  label: '90 d' },
  { value: '180d', label: '6 mo' },
  { value: '365d', label: '1 y'  },
];
const DOW_LABELS  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const STATUS_FILL = {
  active: '#4CAF50', maintenance: '#FF9800', disposed: '#f44336',
  retired: '#795548', lost: '#E91E63', added: '#2196F3',
};

const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;
const fmtNum = (n) => (n ?? 0).toLocaleString();
const fmtBucket = (b) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(b)) return b.slice(5);
  if (/^\d{4}-\d{2}-\d{2} \d{2}/.test(b)) return b.slice(11,16);
  if (/^\d{4}-\d+$/.test(b)) return 'W' + b.split('-')[1];
  return b;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString([], { year:'numeric', month:'short', day:'2-digit' });
};
const fmtAge = (days) => {
  if (days == null) return 'never';
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

// Animated KPI tile. Value can be a number (animates) or a string (renders raw).
const KpiTile = ({ icon, label, value, sublabel, color, accent = false, percent = false, decimals = 0 }) => {
  // If value looks numeric, animate; else render straight through.
  const numeric = typeof value === 'number' && !isNaN(value);
  const animated = useCountUp(numeric ? (percent ? value * 100 : value) : 0, { decimals });
  const display = numeric
    ? (percent ? `${animated.toFixed(1)}%` : animated.toLocaleString(undefined, { maximumFractionDigits: decimals }))
    : (value ?? '—');
  return (
    <div className="stat-card" style={{
      background: accent ? `linear-gradient(135deg, ${color}, ${color}dd)` : 'white',
      color: accent ? 'white' : 'inherit',
    }}>
      <div className="stat-icon" style={{
        background: accent ? 'rgba(255,255,255,0.2)' : color + '22',
        color: accent ? 'white' : color
      }}>{icon}</div>
      <div className="stat-info">
        <div className="stat-value" style={{ color: accent ? 'white' : color }}>{display}</div>
        <div className="stat-label" style={{ color: accent ? 'rgba(255,255,255,0.85)' : undefined }}>{label}</div>
        {sublabel && <div style={{
          fontSize: 11, marginTop: 2,
          color: accent ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)',
        }}>{sublabel}</div>}
      </div>
    </div>
  );
};

const Card = ({ title, icon, children, right, footer }) => (
  <div className="card">
    <div className="card-header">
      <div className="card-title">{icon} {title}</div>
      {right}
    </div>
    {children}
    {footer && (
      <div style={{
        borderTop: '1px solid var(--border-light)', paddingTop: 8, marginTop: 8,
        fontSize: 11, color: 'var(--text-muted)',
      }}>{footer}</div>
    )}
  </div>
);

// ── Heatmap (24 × 7 grid) ────────────────────────────────────────────────────
const Heatmap = ({ grid, max }) => {
  if (!grid?.length) return <div className="empty-state">No activity in this period</div>;
  const cellColor = (count) => {
    if (max === 0 || count === 0) return '#f5f7fa';
    const intensity = Math.min(1, count / max);
    // Subtle blue → bright accent
    const r = Math.round(33  + (33  - 33)  * intensity);
    const g = Math.round(150 + (40  - 150) * (1 - intensity));
    const b = Math.round(243);
    return `rgba(${r}, ${g}, ${b}, ${0.15 + intensity * 0.85})`;
  };
  return (
    <div style={{ overflowX: 'auto', padding: '4px 0' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 2, width: '100%' }}>
        <thead>
          <tr>
            <th></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 400, padding: 0, textAlign: 'center' }}>
                {h % 3 === 0 ? h : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DOW_LABELS.map((d, di) => (
            <tr key={d}>
              <td style={{ fontSize: 11, color: 'var(--text-secondary)', paddingRight: 6, fontWeight: 500 }}>{d}</td>
              {grid[di].map(cell => (
                <td key={cell.hour}
                  title={`${d} ${String(cell.hour).padStart(2,'0')}:00 — ${cell.count} events`}
                  style={{
                    background: cellColor(cell.count),
                    width: 24, height: 22, borderRadius: 3, cursor: 'default',
                    transition: 'transform 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>Less</span>
        {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
          <div key={i} style={{
            width: 14, height: 14, borderRadius: 3,
            background: cellColor(v * (max || 1)),
          }} />
        ))}
        <span>More</span>
        <span style={{ marginLeft: 8 }}>· max {max} events / hour</span>
      </div>
    </div>
  );
};

// ── Asset Journey panel ────────────────────────────────────────────────────
const AssetJourney = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get('/analytics/asset-search', { params: { q: query } });
        setResults(r.data.rows || []);
      } catch {} finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadJourney = async (assetId) => {
    setLoading(true); setJourney(null);
    try {
      const r = await api.get(`/analytics/asset-journey/${assetId}`);
      setJourney(r.data);
    } catch (e) {
      setJourney({ error: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const onPick = (a) => {
    setSelected(a); setQuery(''); setResults([]);
    loadJourney(a.id);
  };

  // Build a single chronological timeline from the three journey sources
  const timeline = useMemo(() => {
    if (!journey || journey.error) return [];
    const items = [];
    if (journey.asset?.created_at) {
      items.push({ at: journey.asset.created_at, kind: 'created', text: 'Asset registered', icon: '➕' });
    }
    for (const m of journey.movements || []) {
      items.push({
        at: m.movement_date, kind: 'move',
        text: `Moved ${m.from_name || '—'} → ${m.to_name || '—'}${m.reason ? ` (${m.reason})` : ''}`,
        sub: m.moved_by ? `by ${m.moved_by}` : null, icon: '↔'
      });
    }
    for (const s of journey.statuses || []) {
      items.push({
        at: s.created_at, kind: 'status',
        text: `Status: ${s.from_status || '?'} → ${s.to_status}${s.reason ? ` (${s.reason})` : ''}`,
        sub: s.changed_by_name ? `by ${s.changed_by_name}` : null, icon: '◐',
      });
    }
    for (const a of journey.audits || []) {
      items.push({
        at: a.scanned_at || a.audit_date, kind: 'audit',
        text: a.scanned ? `✓ Audited in "${a.audit_name || `#${a.audit_id}`}"` : `✗ Missing in "${a.audit_name || `#${a.audit_id}`}"`,
        sub: a.location_name, icon: a.scanned ? '✓' : '✗',
      });
    }
    return items.sort((x, y) => new Date(y.at) - new Date(x.at));
  }, [journey]);

  return (
    <Card title="Asset Journey" icon={<MdRoute style={{ color: 'var(--accent)' }} />}
      right={selected && (
        <button className="btn btn-sm btn-outline" onClick={() => { setSelected(null); setJourney(null); }}>
          Clear
        </button>
      )}>
      {!selected ? (
        <div style={{ position: 'relative' }}>
          <div className="search-bar" style={{ width: '100%' }}>
            <MdSearch />
            <input
              autoFocus placeholder="Search by name, RFID tag, serial, or barcode…"
              value={query} onChange={e => setQuery(e.target.value)} />
          </div>
          {searching && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>}
          {results.length > 0 && (
            <div style={{
              marginTop: 8, border: '1px solid var(--border)', borderRadius: 8,
              maxHeight: 280, overflowY: 'auto', background: 'white',
            }}>
              {results.map(r => (
                <div key={r.id} onClick={() => onPick(r)} style={{
                  padding: '10px 12px', cursor: 'pointer', fontSize: 13,
                  borderBottom: '1px solid var(--border-light)',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f7fa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <div style={{ fontWeight: 600 }}>{r.asset_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 10 }}>
                    <span>RFID: {r.rfid_tag}</span>
                    {r.serial_number && <span>SN: {r.serial_number}</span>}
                    {r.location_name && <span>📍 {r.location_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!query && (
            <div style={{ marginTop: 16, padding: 16, background: '#fafbfd', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              Pick an asset to see its complete history — every move, status change, and audit appearance in one chronological timeline.
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="empty-state">Loading journey…</div>
      ) : journey?.error ? (
        <div className="alert alert-danger">⚠ {journey.error}</div>
      ) : journey ? (
        <div>
          {/* Asset header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(33,150,243,0.05), rgba(33,150,243,0.01))',
            padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid rgba(33,150,243,0.15)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{journey.asset.asset_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  RFID: <span style={{ fontFamily: 'monospace' }}>{journey.asset.rfid_tag}</span>
                  {journey.asset.serial_number && <> · SN: <span style={{ fontFamily: 'monospace' }}>{journey.asset.serial_number}</span></>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
                <div><strong>{journey.summary.total_movements}</strong> moves</div>
                <div><strong>{journey.summary.unique_locations}</strong> locations</div>
                <div><strong>{journey.summary.times_scanned}</strong>/{journey.summary.total_audits} audited</div>
              </div>
            </div>
            {journey.asset.current_location_name && (
              <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-secondary)' }}>
                Currently at: <strong>{journey.asset.current_location_name}</strong>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div style={{ position: 'relative', maxHeight: 380, overflowY: 'auto', paddingLeft: 16 }}>
            <div style={{
              position: 'absolute', left: 22, top: 4, bottom: 4, width: 2, background: 'var(--border)',
            }} />
            {timeline.length === 0 ? (
              <div className="empty-state">No history yet</div>
            ) : timeline.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, position: 'relative' }}>
                <div style={{
                  flexShrink: 0, width: 14, height: 14, borderRadius: '50%',
                  background: t.kind === 'created' ? '#4CAF50'
                            : t.kind === 'move'    ? '#2196F3'
                            : t.kind === 'status'  ? '#FF9800'
                            : t.text.startsWith('✓') ? '#4CAF50' : '#f44336',
                  border: '3px solid white', boxShadow: '0 0 0 2px var(--border)',
                  marginTop: 4, marginLeft: -2,
                }} />
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 500 }}>{t.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                    {new Date(t.at).toLocaleString([], { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {t.sub && ` · ${t.sub}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
};

const Analytics = () => {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kpis, setKpis] = useState(null);
  const [heatmap, setHeatmap] = useState({ grid: [], max: 0 });
  const [lifecycle, setLifecycle] = useState({ points: [] });
  const [stale, setStale] = useState({ rows: [], total: 0 });
  const [movers, setMovers] = useState({ rows: [] });
  const [coverage, setCoverage] = useState({ rows: [] });
  const [leaderboard, setLeaderboard] = useState({ rows: [] });
  const [flow, setFlow] = useState({ rows: [] });
  const [age, setAge] = useState({ rows: [] });
  const [reasons, setReasons] = useState({ rows: [] });
  const [velocity, setVelocity] = useState({ points: [] });
  const [forecast, setForecast] = useState({ rows: [] });
  const [readerHealth, setReaderHealth] = useState({ rows: [] });

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { period };
      const [k, h, l, s, m, c, lb, f, ag, rs, vel, fc, rh] = await Promise.all([
        api.get('/analytics/kpis', { params }),
        api.get('/analytics/activity-heatmap', { params }),
        api.get('/analytics/lifecycle', { params }),
        api.get('/analytics/stale-assets', { params: { days: 60, limit: 10 } }),
        api.get('/analytics/top-movers',   { params }),
        api.get('/analytics/location-coverage'),
        api.get('/analytics/user-leaderboard', { params }),
        api.get('/analytics/location-flow',    { params }),
        api.get('/analytics/asset-age'),
        api.get('/analytics/movement-reasons', { params }),
        api.get('/analytics/audit-velocity',   { params }),
        api.get('/analytics/audit-forecast'),
        api.get('/analytics/reader-health'),
      ]);
      setKpis(k.data); setHeatmap(h.data); setLifecycle(l.data);
      setStale(s.data); setMovers(m.data); setCoverage(c.data);
      setLeaderboard(lb.data); setFlow(f.data);
      setAge(ag.data); setReasons(rs.data); setVelocity(vel.data);
      setForecast(fc.data); setReaderHealth(rh.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const lifeData = useMemo(() =>
    (lifecycle.points || []).map(p => ({ ...p, label: fmtBucket(p.bucket) })),
    [lifecycle.points]
  );
  const velocityData = useMemo(() =>
    (velocity.points || []).map(p => ({
      ...p, label: fmtBucket(p.bucket),
      avg_minutes: p.avg_minutes ? Math.round(p.avg_minutes) : 0,
    })),
    [velocity.points]
  );
  const ageData = useMemo(() => (age.rows || []).filter(r => r.count > 0), [age.rows]);
  const reasonsData = useMemo(() => reasons.rows || [], [reasons.rows]);

  const PIE_COLORS = ['#2563EB','#10B981','#F59E0B','#EF4444','#9333EA','#06B6D4','#8B5CF6','#EC4899','#14B8A6'];

  const periodPicker = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {PERIODS.map(p => (
        <button key={p.value} onClick={() => setPeriod(p.value)}
          className={`btn btn-sm ${period === p.value ? 'btn-primary' : 'btn-outline'}`}
          style={{ minWidth: 50 }}>
          {p.label}
        </button>
      ))}
      <button className="btn btn-sm btn-outline" onClick={loadAll} title="Refresh">
        <MdRefresh /> Refresh
      </button>
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdInsights /> Analytics</div>
          <div className="page-subtitle">Behavioural insights, lifecycle composition and per-asset journeys — distinct from the Dashboard's "right now" view</div>
        </div>
        <div>{periodPicker}</div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>⚠ Failed to load analytics: {error}</div>}

      {/* ─── KPI tiles (different from Dashboard) ─────────────────────────── */}
      {loading && !kpis ? (
        <div className="stats-grid">
          {Array.from({ length: 6 }, (_, i) => <Skeleton.Tile key={i} />)}
        </div>
      ) : (
        <div className="stats-grid">
          <KpiTile icon={<MdSpeed />} label="Asset utilisation" color="#2563EB" accent percent decimals={1}
            value={kpis?.utilisation}
            sublabel={kpis ? `${fmtNum(kpis.activeAssets)} / ${fmtNum(kpis.totalAssets)} active` : ''} />
          <KpiTile icon={<MdAssignment />} label="Audit accuracy" color="#10B981" percent decimals={1}
            value={kpis?.auditAccuracy}
            sublabel={kpis ? `avg over ${kpis.auditCount} audits` : ''} />
          <KpiTile icon={<MdTrendingUp />} label="Movement velocity" color="#9333EA" decimals={3}
            value={kpis?.movementVelocity}
            sublabel="moves / asset / day" />
          <KpiTile icon={<MdHourglassEmpty />} label="Stale assets" color="#F59E0B"
            value={kpis?.staleAssets}
            sublabel={kpis ? `${fmtPct(kpis.staleRatio)} of total · 60d+ untouched` : ''} />
          <KpiTile icon={<MdLocationOn />} label="Location coverage" color="#06B6D4" percent decimals={1}
            value={kpis?.locationCoverage}
            sublabel={kpis ? `${kpis.coveredLocations} / ${kpis.totalLocations} audited` : ''} />
          <KpiTile icon={<MdCompareArrows />} label="Avg audit time" color="#0F2A4A"
            value={kpis ? `${kpis.avgAuditMinutes} min` : '—'}
            sublabel={kpis ? `${kpis.newAssetsInPeriod} new assets in period` : ''} />
        </div>
      )}

      {/* ─── Row 1: Heatmap + Lifecycle ───────────────────────────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Activity heatmap (hour × day-of-week)"
          icon={<MdInsights style={{ color: 'var(--accent)' }} />}
          footer="Combines audit scans, asset movements, and status changes. Identifies your real working hours.">
          <Heatmap grid={heatmap.grid} max={heatmap.max} />
        </Card>

        <Card title="Lifecycle composition" icon={<MdInventory2 style={{ color: 'var(--success)' }} />}
          footer="New registrations vs status transitions, bucketed by week or day depending on period.">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={lifeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="added"       stackId="a" fill={STATUS_FILL.added}       name="Added" />
                <Bar dataKey="active"      stackId="a" fill={STATUS_FILL.active}      name="→ Active" />
                <Bar dataKey="maintenance" stackId="a" fill={STATUS_FILL.maintenance} name="→ Maintenance" />
                <Bar dataKey="disposed"    stackId="a" fill={STATUS_FILL.disposed}    name="→ Disposed" />
                <Bar dataKey="retired"     stackId="a" fill={STATUS_FILL.retired}     name="→ Retired" />
                <Bar dataKey="lost"        stackId="a" fill={STATUS_FILL.lost}        name="→ Lost" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ─── Row 2: Stale + Top movers ───────────────────────────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title={`Stale assets (${stale.total} total — never moved/audited in 60+ days)`}
          icon={<MdHourglassEmpty style={{ color: 'var(--warning)' }} />}
          right={<ExportButtons rows={stale.rows} filename={`stale-assets-${period}`}
            title="Stale Assets" subtitle={`Untouched for 60+ days · ${stale.total} total`}
            columns={[
              { key: 'asset_name', label: 'Asset' },
              { key: 'rfid_tag',   label: 'RFID' },
              { key: 'serial_number', label: 'Serial' },
              { key: 'category',   label: 'Category' },
              { key: 'status',     label: 'Status' },
              { key: 'location_name', label: 'Location' },
              { key: 'created_at', label: 'Created',     format: v => v ? new Date(v).toLocaleDateString() : '' },
              { key: 'last_moved', label: 'Last moved',  format: v => v ? new Date(v).toLocaleDateString() : 'never' },
              { key: 'last_scanned', label: 'Last scanned', format: v => v ? new Date(v).toLocaleDateString() : 'never' },
            ]} />}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Location</th>
                  <th>Created</th>
                  <th>Last moved</th>
                  <th>Last scanned</th>
                </tr>
              </thead>
              <tbody>
                {(stale.rows || []).length === 0 ? (
                  <tr><td colSpan={5} className="empty-state">No stale assets — nice!</td></tr>
                ) : stale.rows.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.asset_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{r.rfid_tag}</div>
                    </td>
                    <td>{r.location_name || '—'}</td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td style={{ color: r.last_moved ? undefined : 'var(--text-muted)' }}>
                      {r.last_moved ? fmtDate(r.last_moved) : 'never'}
                    </td>
                    <td style={{ color: r.last_scanned ? undefined : 'var(--text-muted)' }}>
                      {r.last_scanned ? fmtDate(r.last_scanned) : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Top-moved assets in period" icon={<MdSwapHoriz style={{ color: 'var(--accent)' }} />}
          right={<ExportButtons rows={movers.rows} filename={`top-movers-${period}`}
            title="Top-moved Assets" subtitle={`Period: ${period}`}
            columns={[
              { key: 'asset_name', label: 'Asset' },
              { key: 'rfid_tag',   label: 'RFID' },
              { key: 'category',   label: 'Category' },
              { key: 'status',     label: 'Status' },
              { key: 'current_location', label: 'Current location' },
              { key: 'movement_count', label: 'Moves' },
              { key: 'unique_locations', label: 'Distinct locations' },
              { key: 'last_movement', label: 'Last move', format: v => v ? new Date(v).toLocaleString() : '' },
            ]} />}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Current loc</th>
                  <th style={{ textAlign: 'right' }}>Moves</th>
                  <th style={{ textAlign: 'right' }}>Locations</th>
                </tr>
              </thead>
              <tbody>
                {(movers.rows || []).length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">No movements in this period</td></tr>
                ) : movers.rows.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.asset_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{r.rfid_tag}</div>
                    </td>
                    <td>{r.current_location || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--accent)' }}>{r.movement_count}</td>
                    <td style={{ textAlign: 'right' }}>{r.unique_locations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ─── Row 3: Coverage + Leaderboard ───────────────────────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Location coverage" icon={<MdLocationOn style={{ color: 'var(--success)' }} />}
          footer="When was each location last audited, and how accurate was that audit?"
          right={<ExportButtons rows={coverage.rows} filename={`location-coverage`}
            title="Location Coverage"
            columns={[
              { key: 'location_name', label: 'Location' },
              { key: 'location_code', label: 'Code' },
              { key: 'asset_count',   label: 'Assets' },
              { key: 'last_audit_date', label: 'Last audit', format: v => v ? new Date(v).toLocaleDateString() : 'never' },
              { key: 'days_since_audit', label: 'Days since', format: v => v == null ? 'never' : v },
              { key: 'last_audit_accuracy', label: 'Accuracy', format: v => v == null ? '—' : `${(v*100).toFixed(1)}%` },
            ]} />}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Location</th>
                  <th style={{ textAlign: 'right' }}>Assets</th>
                  <th style={{ textAlign: 'right' }}>Last audit</th>
                  <th style={{ textAlign: 'right' }}>Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {(coverage.rows || []).length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">No locations</td></tr>
                ) : coverage.rows.map(r => {
                  const acc = r.last_audit_accuracy;
                  const ageColor = r.days_since_audit == null ? 'var(--danger)'
                                : r.days_since_audit > 90    ? 'var(--danger)'
                                : r.days_since_audit > 30    ? 'var(--warning)'
                                : 'var(--success)';
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.location_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{r.location_code}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(r.asset_count)}</td>
                      <td style={{ textAlign: 'right', color: ageColor, fontWeight: 600 }}>{fmtAge(r.days_since_audit)}</td>
                      <td style={{ textAlign: 'right',
                        color: acc == null ? 'var(--text-muted)'
                              : acc >= 0.9 ? 'var(--success)'
                              : acc >= 0.6 ? 'var(--warning)' : 'var(--danger)',
                        fontWeight: 600 }}>
                        {acc == null ? '—' : fmtPct(acc)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="User leaderboard" icon={<MdEmojiEvents style={{ color: 'var(--warning)' }} />}
          right={<ExportButtons rows={leaderboard.rows} filename={`user-leaderboard-${period}`}
            title="User Leaderboard" subtitle={`Period: ${period}`}
            columns={[
              { key: 'username',  label: 'User' },
              { key: 'events',    label: 'Total events' },
              { key: 'creates',   label: 'Created' },
              { key: 'edits',     label: 'Edited' },
              { key: 'deletes',   label: 'Deleted' },
              { key: 'audit_actions', label: 'Audit actions' },
              { key: 'last_active', label: 'Last active', format: v => v ? new Date(v).toLocaleString() : '' },
            ]} />}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Audit acts</th>
                </tr>
              </thead>
              <tbody>
                {(leaderboard.rows || []).length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">No activity</td></tr>
                ) : leaderboard.rows.map((r, i) => (
                  <tr key={r.username}>
                    <td style={{ fontSize: 18, color: i === 0 ? '#FFC107' : i === 1 ? '#90A4AE' : i === 2 ? '#A1887F' : 'var(--text-muted)' }}>
                      {i < 3 ? '🥇🥈🥉'[i] : `#${i+1}`}
                    </td>
                    <td><strong>{r.username}</strong></td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.events}</td>
                    <td style={{ textAlign: 'right' }}>{r.audit_actions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ─── Row 4: Location flow + Asset Journey ───────────────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 16, marginTop: 16 }}>
        <Card title="Top movement paths" icon={<MdCompareArrows style={{ color: 'var(--accent)' }} />}
          footer="Most common from→to location pairs in the period.">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>From</th>
                  <th></th>
                  <th>To</th>
                  <th style={{ textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {(flow.rows || []).length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">No inter-location movements yet</td></tr>
                ) : flow.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.from_name || `#${r.from_location_id}`}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 16 }}>→</td>
                    <td>{r.to_name || `#${r.to_location_id}`}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--accent)' }}>{r.flow_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <AssetJourney />
      </div>

      {/* ─── Row 5: Asset age distribution + Movement reasons ────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Asset age distribution"
          icon={<MdAccessTime style={{ color: 'var(--accent)' }} />}
          footer="Bucketed by purchase_date when present, otherwise the registration date."
          right={<ExportButtons rows={ageData} filename="asset-age-distribution"
            title="Asset Age Distribution"
            columns={[
              { key: 'label', label: 'Age bucket' },
              { key: 'count', label: 'Asset count' },
            ]} />}>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill="var(--accent)" radius={[6, 6, 0, 0]} maxBarSize={46}>
                  {ageData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Movement reasons breakdown"
          icon={<MdEventNote style={{ color: 'var(--warning)' }} />}
          footer="Top 20 reasons recorded on asset movements in this period."
          right={<ExportButtons rows={reasonsData} filename={`movement-reasons-${period}`}
            title="Movement Reasons"
            columns={[
              { key: 'reason', label: 'Reason' },
              { key: 'count', label: 'Movements' },
            ]} />}>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={reasonsData} dataKey="count" nameKey="reason"
                     cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={2}>
                  {reasonsData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* ─── Row 6: Audit velocity trend + Audit forecast ──────────────── */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Audit velocity & avg duration"
          icon={<MdTrendingUp style={{ color: 'var(--success)' }} />}
          footer="Bars: audits started vs completed. Line: average completion time in minutes."
          right={<ExportButtons rows={velocityData} filename={`audit-velocity-${period}`}
            title="Audit Velocity"
            columns={[
              { key: 'label', label: 'Bucket' },
              { key: 'started', label: 'Started' },
              { key: 'completed', label: 'Completed' },
              { key: 'avg_minutes', label: 'Avg minutes' },
            ]} />}>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={velocityData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="started"   fill="#94A3B8" name="Started" />
                <Bar yAxisId="left" dataKey="completed" fill="#10B981" name="Completed" />
                <Line yAxisId="right" type="monotone" dataKey="avg_minutes" stroke="#F59E0B" strokeWidth={2} name="Avg min" dot={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Audit forecast — locations needing audit"
          icon={<MdCalendarMonth style={{ color: 'var(--danger)' }} />}
          footer="Priority = days-since-last-audit × log(asset count). Higher = more overdue."
          right={<ExportButtons rows={forecast.rows} filename="audit-forecast"
            title="Audit Forecast — Priority Order"
            columns={[
              { key: 'location_name', label: 'Location' },
              { key: 'location_code', label: 'Code' },
              { key: 'asset_count',   label: 'Assets' },
              { key: 'last_audit',    label: 'Last audit', format: v => v ? new Date(v).toLocaleDateString() : 'never' },
              { key: 'days_since_audit', label: 'Days since', format: v => v == null ? '—' : v },
              { key: 'priority',      label: 'Priority' },
            ]} />}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Location</th>
                  <th style={{ textAlign: 'right' }}>Assets</th>
                  <th style={{ textAlign: 'right' }}>Days since</th>
                  <th style={{ textAlign: 'right' }}>Priority</th>
                </tr>
              </thead>
              <tbody>
                {(forecast.rows || []).length === 0 ? (
                  <tr><td colSpan={5} className="empty-state">All locations recently audited!</td></tr>
                ) : forecast.rows.map((r, i) => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.location_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{r.location_code}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.asset_count}</td>
                    <td style={{ textAlign: 'right',
                      color: r.days_since_audit == null || r.days_since_audit > 90 ? 'var(--danger)'
                            : r.days_since_audit > 30 ? 'var(--warning)' : 'var(--success)',
                      fontWeight: 600 }}>{r.days_since_audit == null ? 'never' : r.days_since_audit}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700,
                      color: i < 3 ? 'var(--danger)' : 'var(--text-secondary)' }}>{r.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ─── Row 7: Reader health (only if any readers exist) ───────────── */}
      {readerHealth.rows && readerHealth.rows.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Card title="Reader health" icon={<MdRouter style={{ color: 'var(--info)' }} />}
            footer="Live status from the per-reader health check loop (30 s)."
            right={<ExportButtons rows={readerHealth.rows} filename="reader-health"
              title="Reader Health"
              columns={[
                { key: 'reader_name', label: 'Reader' },
                { key: 'ip_address', label: 'IP' },
                { key: 'port', label: 'Port' },
                { key: 'status', label: 'Status' },
                { key: 'last_seen', label: 'Last seen', format: v => v ? new Date(v).toLocaleString() : '—' },
              ]} />}>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Reader</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {readerHealth.rows.map(r => {
                    const cls = r.status === 'online' ? 'var(--success)'
                              : r.status === 'offline' ? 'var(--danger)' : 'var(--warning)';
                    return (
                      <tr key={r.id}>
                        <td><strong>{r.reader_name}</strong></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.ip_address}:{r.port}</td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            color: cls, fontWeight: 600, fontSize: 12,
                          }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: cls,
                              boxShadow: r.status === 'online' ? `0 0 6px ${cls}` : 'none' }} />
                            {(r.status || 'unknown').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {r.last_seen ? new Date(r.last_seen).toLocaleString() : 'never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Analytics;
